import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { apiPrivate } from "@/lib/api";
import { Edit2, Check, X, Mail, Phone, MapPin, Building, ShieldCheck, Loader2 } from "lucide-react";

export default function Profilo() {
    const { user, refreshUser } = useAuth();
    const [editing, setEditing] = useState(null);
    const [values, setValues] = useState({});
    const [savingField, setSavingField] = useState(null);
    const [emailDialog, setEmailDialog] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [emailMsg, setEmailMsg] = useState(null);

    useEffect(() => {
        if (user) setValues({
            pec: user.pec || "",
            telefono: user.telefono || "",
            indirizzo: user.indirizzo || "",
            interesse_vendita: user.interesse_vendita || false,
        });
    }, [user]);

    const startEdit = (k) => setEditing(k);
    const cancelEdit = (k) => { setEditing(null); setValues((s) => ({ ...s, [k]: user[k] || "" })); };

    const saveField = async (k) => {
        setSavingField(k);
        try {
            await apiPrivate.patch("/soci/me", { [k]: values[k] });
            await refreshUser();
            setEditing(null);
        } catch (e) { /* keep editing on error */ }
        finally { setSavingField(null); }
    };

    const requestEmailChange = async () => {
        setEmailMsg(null);
        try {
            await apiPrivate.post("/soci/me/change-email", { new_email: newEmail });
            setEmailMsg({ type: "ok", text: `Email di verifica inviata a ${newEmail}. Conferma il cambio dal link ricevuto.` });
            setNewEmail("");
        } catch (err) {
            setEmailMsg({ type: "err", text: err?.response?.data?.detail || "Errore" });
        }
    };

    if (!user) return null;

    return (
        <AppLayout>
            <div className="max-w-5xl mx-auto" data-testid="profilo-page">
                <span className="label-tag">— Anagrafica</span>
                <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tighter">Il mio <span className="font-serif-italic text-acid">profilo</span>.</h1>
                <p className="text-zinc-400 mt-4 max-w-xl">I dati identificativi sono gestiti solo dall'amministratore. I contatti puoi aggiornarli in autonomia.</p>

                {/* Read-only data */}
                <section className="mt-10">
                    <div className="flex items-center gap-2 mb-4">
                        <ShieldCheck className="h-4 w-4 text-zinc-500" />
                        <span className="label-tag">Dati identificativi (gestiti dal team)</span>
                    </div>
                    <div className="bento p-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
                        {/* Ragione sociale (aziende) o Nome+Cognome (persone fisiche) */}
                        {user.ragione_sociale ? (
                            <ReadField label="Denominazione / Ragione sociale" value={user.ragione_sociale} testid="rf-ragione" />
                        ) : (
                            <ReadField label="Nome e Cognome" value={`${user.nome || ""} ${user.cognome || ""}`.trim() || "—"} testid="rf-nome" />
                        )}
                        <ReadField label="Email di accesso" value={user.email} testid="rf-email" suffix={
                            <button className="text-acid text-xs uppercase tracking-widest font-grotesk hover:underline" onClick={() => setEmailDialog(true)} data-testid="change-email-btn">Cambia</button>
                        } />

                        {/* P.IVA — solo se presente */}
                        {user.partita_iva && (
                            <ReadField label="Partita IVA" value={user.partita_iva} testid="rf-piva" />
                        )}
                        {/* Codice fiscale — solo se presente */}
                        {user.codice_fiscale && (
                            <ReadField label="Codice fiscale" value={user.codice_fiscale} testid="rf-cf" />
                        )}

                        {/* Data nascita — solo persone fisiche */}
                        {user.data_nascita && (
                            <ReadField label="Data di nascita" value={user.data_nascita} testid="rf-data" />
                        )}
                        {user.sesso && (
                            <ReadField label="Sesso" value={user.sesso === "M" ? "Maschile" : user.sesso === "F" ? "Femminile" : user.sesso} testid="rf-sesso" />
                        )}

                        {/* Localizzazione — persone fisiche */}
                        {(user.citta || user.provincia || user.cap || user.nazione) && (
                            <ReadField
                                label="Residenza"
                                value={[user.citta, user.provincia && `(${user.provincia})`, user.cap, user.nazione]
                                    .filter(Boolean)
                                    .join(" ") || "—"}
                                testid="rf-residenza"
                            />
                        )}

                        {/* Email secondaria — aziende */}
                        {user.email_secondaria && (
                            <ReadField label="Email secondaria" value={user.email_secondaria} testid="rf-email2" />
                        )}
                    </div>
                </section>

                {/* Socio abbinato — solo aziende */}
                {(user.socio_abbinato_nome || user.socio_abbinato_tipo || user.socio_abbinato_cf) && (
                    <section className="mt-10">
                        <div className="flex items-center gap-2 mb-4">
                            <Building className="h-4 w-4 text-zinc-500" />
                            <span className="label-tag">Socio abbinato</span>
                        </div>
                        <div className="bento p-8 grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-6">
                            {user.socio_abbinato_nome && (
                                <ReadField label="Nome" value={user.socio_abbinato_nome} testid="rf-abb-nome" />
                            )}
                            {user.socio_abbinato_tipo && (
                                <ReadField label="Tipo abbinamento" value={user.socio_abbinato_tipo} testid="rf-abb-tipo" />
                            )}
                            {user.socio_abbinato_cf && (
                                <ReadField label="CF / P.IVA" value={user.socio_abbinato_cf} testid="rf-abb-cf" />
                            )}
                        </div>
                    </section>
                )}

                {/* Editable contacts */}
                <section className="mt-10">
                    <div className="flex items-center gap-2 mb-4">
                        <Edit2 className="h-4 w-4 text-acid" />
                        <span className="label-tag">Dati di contatto (editabili)</span>
                    </div>
                    <div className="bento p-8 divide-y divide-zinc-900">
                        <EditableField k="pec" label="PEC" icon={Mail} editing={editing} startEdit={startEdit} cancelEdit={cancelEdit} saveField={saveField} values={values} setValues={setValues} savingField={savingField} type="email" testid="ef-pec" />
                        <EditableField k="telefono" label="Telefono" icon={Phone} editing={editing} startEdit={startEdit} cancelEdit={cancelEdit} saveField={saveField} values={values} setValues={setValues} savingField={savingField} testid="ef-tel" />
                        <EditableField k="indirizzo" label="Indirizzo / Sede legale" icon={MapPin} editing={editing} startEdit={startEdit} cancelEdit={cancelEdit} saveField={saveField} values={values} setValues={setValues} savingField={savingField} testid="ef-addr" />
                    </div>
                </section>

                {emailDialog && (
                    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEmailDialog(false)} data-testid="change-email-modal">
                        <div className="bg-[#0a0a0a] border border-zinc-800 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
                            <span className="label-tag">— Cambio email</span>
                            <h3 className="font-display text-3xl tracking-tighter mt-3">Aggiorna l'email di accesso.</h3>
                            <p className="text-zinc-400 text-sm mt-3">Ti invieremo un link di verifica al nuovo indirizzo. Il vecchio indirizzo riceverà una notifica di avviso.</p>
                            <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                placeholder="nuova.email@example.it"
                                className="mt-6 w-full bg-[#0F0F0F] border border-zinc-800 text-white px-4 py-3 font-grotesk focus:outline-none focus:border-acid"
                                data-testid="new-email-input"
                            />
                            {emailMsg && (
                                <div className={`mt-3 text-sm px-3 py-2 ${emailMsg.type === "ok" ? "border border-acid/40 bg-acid/5 text-acid" : "border border-magenta/40 bg-magenta/10 text-magenta"}`} data-testid="change-email-msg">
                                    {emailMsg.text}
                                </div>
                            )}
                            <div className="flex gap-3 mt-6">
                                <button onClick={requestEmailChange} className="btn-rebel flex-1 justify-center" data-testid="confirm-change-email">Invia verifica</button>
                                <button onClick={() => setEmailDialog(false)} className="btn-ghost" data-testid="cancel-change-email">Annulla</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
    );
}

function ReadField({ label, value, suffix, testid }) {
    return (
        <div data-testid={testid}>
            <div className="label-tag">{label}</div>
            <div className="mt-2 font-grotesk text-white flex items-center gap-3">{value || "—"}{suffix}</div>
        </div>
    );
}
function EditableField({ k, label, icon: Icon, editing, startEdit, cancelEdit, saveField, values, setValues, savingField, type = "text", testid }) {
    const isEditing = editing === k;
    return (
        <div className="py-4 flex items-center justify-between gap-4" data-testid={testid}>
            <div className="flex items-start gap-4 flex-1 min-w-0">
                <Icon className="h-4 w-4 text-zinc-500 mt-1" />
                <div className="flex-1 min-w-0">
                    <div className="label-tag">{label}</div>
                    {isEditing ? (
                        <input
                            type={type}
                            value={values[k]}
                            onChange={(e) => setValues((s) => ({ ...s, [k]: e.target.value }))}
                            className="mt-2 w-full bg-[#0F0F0F] border border-acid text-white px-3 py-2 font-grotesk focus:outline-none"
                            data-testid={`${testid}-input`}
                            autoFocus
                        />
                    ) : (
                        <div className="mt-2 font-grotesk text-white truncate">{values[k] || <span className="text-zinc-600">Non impostato</span>}</div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2">
                {isEditing ? (
                    <>
                        <button onClick={() => saveField(k)} disabled={savingField === k} className="p-2 border border-acid text-acid hover:bg-acid hover:text-black transition" data-testid={`${testid}-save`}>
                            {savingField === k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button onClick={() => cancelEdit(k)} className="p-2 border border-zinc-800 text-zinc-500 hover:text-white" data-testid={`${testid}-cancel`}>
                            <X className="h-4 w-4" />
                        </button>
                    </>
                ) : (
                    <button onClick={() => startEdit(k)} className="text-xs uppercase tracking-widest font-grotesk text-zinc-500 hover:text-acid" data-testid={`${testid}-edit`}>Modifica</button>
                )}
            </div>
        </div>
    );
}
